import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import Spinner from '../../../../../components/Spinner';
import Button from '../../../../../components/Button';
import Table from '../../../../../components/TableBox/Table';
import { TableContext } from '../../../../../contexts/TableContext';
import useFetch from '../../../../../hooks/useFetch';
import DashboardService from '../../../../../services/dashboard.service';
import { buildHeadCells, formatNumber } from '../../../../../utils/methods';
import { VALIDATION_FIELD_MAPPING } from '../../../../../utils/appConstant';
import { SortOrder } from '../../../../../enums';
import { MonthYearValue } from './MonthYearFilter';
import { ViewMode } from './ViewModeToggle';
import ZoneWiseRow from './ZoneWiseRow';

interface ZoneWiseSecondaryClosingProps {
  period: MonthYearValue;
  viewMode: ViewMode;
}

interface ZoneRow {
  zone_code: string;
  zone_name: string;
  issued_qty: number;
  issued_qty_value: number;
  closing_qty: number;
  closing_qty_value: number;
}

const ALL_ZONES: Pick<ZoneRow, 'zone_code' | 'zone_name'>[] = [
  { zone_code: 'EAST', zone_name: 'East' },
  { zone_code: 'WEST', zone_name: 'West' },
  { zone_code: 'NORTH', zone_name: 'North' },
  { zone_code: 'SOUTH', zone_name: 'South' },
];

const SECONDARY_COLOR = '#fdb528';
const CLOSING_COLOR = '#009688';

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="card shadow-sm p-3" style={{ minWidth: 160 }}>
        <p className="fw-semibold mb-2 text-heading">{label}</p>
        {payload.map((entry: any) => (
          <p key={entry.name} className="mb-1 small" style={{ color: entry.color }}>
            {entry.name}: <strong>{formatNumber(entry.value)}</strong>
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const renderLegend = ({ payload }: any) => (
  <div className="d-flex justify-content-end gap-3 pt-2">
    {payload.map((entry: any) => (
      <span key={entry.value} className="d-flex align-items-center gap-2 small">
        <span
          style={{
            display: 'inline-block',
            width: 10,
            height: 10,
            borderRadius: '50%',
            background: entry.color,
            flexShrink: 0,
          }}
        />
        <span>{entry.value}</span>
      </span>
    ))}
  </div>
);

type DisplayMode = 'chart' | 'table';

const headCells = buildHeadCells(
  [VALIDATION_FIELD_MAPPING.zone, VALIDATION_FIELD_MAPPING.secondary, VALIDATION_FIELD_MAPPING.closing],
  'zoneWiseList'
);

const ZoneWiseSecondaryClosing: React.FC<ZoneWiseSecondaryClosingProps> = ({ period, viewMode }) => {
  const { t } = useTranslation();
  const [displayMode, setDisplayMode] = useState<DisplayMode>('chart');
  const [rows, setRows] = useState<ZoneRow[]>([]);

  const { loading } = useFetch({
    service: DashboardService,
    func: 'zoneWiseSecondaryClosing',
    payload: { month: period.month, year: period.year },
    dependency: [period.month, period.year],
    onSuccess: (response: ZoneRow[]) => {
      const dataMap = new Map((response || []).map((r) => [r.zone_code, r]));
      const merged = ALL_ZONES.map((z) => dataMap.get(z.zone_code) ?? {
        ...z,
        issued_qty: 0,
        issued_qty_value: 0,
        closing_qty: 0,
        closing_qty_value: 0,
      });
      setRows(merged);
    },
  });

  const secondaryKey = t('dashboard.pageTexts.zoneWise.secondary');
  const closingKey = t('dashboard.pageTexts.zoneWise.closing');

  const chartData = rows.map((r) => ({
    zone: r.zone_name,
    [secondaryKey]: viewMode === 'value' ? r.issued_qty_value : r.issued_qty,
    [closingKey]: viewMode === 'value' ? r.closing_qty_value : r.closing_qty,
  }));

  const tableValues = useMemo(() => ({
    headCells,
    row: ZoneWiseRow,
    data: rows,
    total: rows.length,
    loading,
    page: 1,
    perPage: rows.length,
    order: SortOrder.asc,
    orderBy: VALIDATION_FIELD_MAPPING.zone,
    setPage: () => {},
    setPerPage: () => {},
    setOrder: () => {},
    setOrderBy: () => {},
    rowProps: { viewMode },
    noDataFoundMessage: '',
  }), [rows, loading, viewMode]);

  const spinner = (
    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 260 }}>
      <Spinner variant="primary" />
    </div>
  );

  return (
    <div className="col-12 col-xl-6">
      <div className="card h-100">
        <div className="card-header d-flex align-items-center justify-content-between">
          <h5 className="card-title mb-0">{t('dashboard.pageTexts.zoneWise.title')}</h5>
          <div className="btn-group" role="group">
            <Button
              className={`btn btn-sm btn-icon ${displayMode === 'chart' ? 'btn-primary' : 'btn-outline-primary'}`}
              icon={<i className="ri-bar-chart-2-line ri-20px" />}
              onClick={() => setDisplayMode('chart')}
            />
            <Button
              className={`btn btn-sm btn-icon ${displayMode === 'table' ? 'btn-primary' : 'btn-outline-primary'}`}
              icon={<i className="ri-table-line ri-20px" />}
              onClick={() => setDisplayMode('table')}
            />
          </div>
        </div>

        {displayMode === 'chart' && (
          <div className="card-body">
            {loading ? spinner : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={chartData} margin={{ top: 8, right: 0, left: 0, bottom: 0 }} barCategoryGap="35%">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e8e8e8" vertical={false} />
                  <XAxis dataKey="zone" tick={{ fontSize: 13 }} axisLine={false} tickLine={false} />
                  <YAxis
                    tick={{ fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v) => formatNumber(v)}
                    width={70}
                  />
                  <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
                  <Legend
                    layout="horizontal"
                    align="right"
                    verticalAlign="bottom"
                    content={renderLegend}
                  />
                  <Bar dataKey={secondaryKey} fill={SECONDARY_COLOR} radius={[4, 4, 0, 0]} maxBarSize={40} />
                  <Bar dataKey={closingKey} fill={CLOSING_COLOR} radius={[4, 4, 0, 0]} maxBarSize={40} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        )}

        {displayMode === 'table' && (
          loading ? (
            <div className="card-body">{spinner}</div>
          ) : (
            <TableContext.Provider value={tableValues}>
              <div className="card-datatable table-responsive">
                <div className="dataTables_wrapper dt-bootstrap5 no-footer">
                  <Table />
                </div>
              </div>
            </TableContext.Provider>
          )
        )}
      </div>
    </div>
  );
};

export default ZoneWiseSecondaryClosing;
